/**
 * Sinnbilder für die Funktionseinheiten eines Lüftungsgeräts.
 *
 * Sie sind absichtlich doppelt so groß wie die übrigen Symbole: Eine Gerätefolge
 * aus zwanzig Einheiten liest man nicht Wort für Wort, sondern man erkennt sie —
 * Filter, Erhitzer, Ventilator, Filter. Dafür muss das Bild groß genug sein, um
 * auf einen Blick zu wirken.
 *
 * Gezeichnet in Anlehnung an die Sinnbilder der Herstellerunterlagen, damit wer
 * das Datenblatt kennt sie wiedererkennt. Alle mit `currentColor`, damit sie der
 * Textfarbe folgen und in beiden Darstellungen tragen.
 */

const G = 32;

type Eigenschaften = { size?: number; title?: string };

function Rahmen({
  size = G, title, children,
}: Eigenschaften & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      style={{ flexShrink: 0 }}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

/** Gehäuse als gemeinsamer Rahmen — jede Einheit sitzt in einem. */
const Gehaeuse = <rect x="3" y="6" width="26" height="20" rx="1.5" opacity={0.45} />;

/** Anschlussrahmen: Flansch mit Luftöffnung. */
export const IconRahmen = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Anschlussrahmen'}>
    {Gehaeuse}
    <rect x="7" y="10" width="18" height="12" rx="1" />
    <path d="M11 10v12M21 10v12" opacity={0.5} />
  </Rahmen>
);

/** Klappe: Jalousieblätter im Gehäuse. */
export const IconKlappe = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Klappe'}>
    {Gehaeuse}
    <path d="M7 11l18 3M7 16l18 3M7 21l18 3" />
  </Rahmen>
);

/** Filter: Taschenfilter, das Zickzack ist das Filtermedium. */
export const IconFilter = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Filter'}>
    {Gehaeuse}
    <path d="M8 9v14M8 9l6 3.5-6 3.5M8 16l6 3.5-6 3.5" />
    <path d="M17 9v14M17 9l6 3.5-6 3.5M17 16l6 3.5-6 3.5" opacity={0.6} />
  </Rahmen>
);

/** Schalldämpfer: Kulissen längs der Strömung. */
export const IconSchall = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Schalldämpfer'}>
    {Gehaeuse}
    <path d="M8 9v14M13 9v14M18 9v14M23 9v14" />
  </Rahmen>
);

/** Erhitzer: Register mit Wärmesymbol. */
export const IconErhitzer = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Erhitzer'}>
    {Gehaeuse}
    <rect x="8" y="10" width="16" height="12" rx="1" />
    <path d="M8 10l16 12M24 10L8 22" opacity={0.55} />
  </Rahmen>
);

/** Kühler: Register mit Tropfen — anders als der Erhitzer schlägt er Wasser ab. */
export const IconKuehler = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Kühler'}>
    {Gehaeuse}
    <rect x="8" y="10" width="16" height="12" rx="1" />
    <path d="M8 10l16 12M24 10L8 22" opacity={0.55} />
    <path d="M16 24.5c-1 0-1.7-.8-1.7-1.7 0-.9 1.7-2.8 1.7-2.8s1.7 1.9 1.7 2.8c0 .9-.8 1.7-1.7 1.7z"
          fill="currentColor" stroke="none" opacity={0.8} />
  </Rahmen>
);

/** Change-Over: ein Register für Heizen UND Kühlen, daher der Wechselpfeil. */
export const IconChangeOver = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Change-Over'}>
    {Gehaeuse}
    <rect x="8" y="10" width="16" height="12" rx="1" />
    <path d="M8 10l16 12M24 10L8 22" opacity={0.4} />
    <path d="M11 14h10l-2.5-2.5M21 18H11l2.5 2.5" strokeWidth={1.6} />
  </Rahmen>
);

/** Tropfenabscheider: Lamellen mit abfallenden Tropfen. */
export const IconTropfen = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Tropfenabscheider'}>
    {Gehaeuse}
    <path d="M10 9c2 2.3 2 4.7 0 7s-2 4.7 0 7M16 9c2 2.3 2 4.7 0 7s-2 4.7 0 7M22 9c2 2.3 2 4.7 0 7s-2 4.7 0 7" />
  </Rahmen>
);

/** Ventilator: Freirad. Der einzige, der Druck liefert statt verbraucht. */
export const IconVentilator = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Ventilator'}>
    {Gehaeuse}
    <circle cx="16" cy="16" r="7" />
    <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none" />
    <path d="M16 9.5c2.6 1.4 3.4 3.6 2 5.2M22.5 16c-1.4 2.6-3.6 3.4-5.2 2M16 22.5c-2.6-1.4-3.4-3.6-2-5.2M9.5 16c1.4-2.6 3.6-3.4 5.2-2" />
  </Rahmen>
);

/** Wärmerückgewinnung: zwei Kreise, die Wärme tauschen. */
export const IconWrg = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Wärmerückgewinnung'}>
    {Gehaeuse}
    <rect x="7" y="9" width="7" height="14" rx="1" />
    <rect x="18" y="9" width="7" height="14" rx="1" />
    <path d="M14 12.5h4M18 19.5h-4" strokeWidth={1.6} />
    <path d="M16.6 11.1L18 12.5l-1.4 1.4M15.4 21l-1.4-1.5 1.4-1.4" />
  </Rahmen>
);

/** Befeuchter: Düsen mit Sprühbild. */
export const IconBefeuchter = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Befeuchter'}>
    {Gehaeuse}
    <path d="M9 9v14" />
    <circle cx="9" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="9" cy="16" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="9" cy="20" r="1.1" fill="currentColor" stroke="none" />
    <path d="M12 12h5M12 16h8M12 20h5" opacity={0.6} strokeDasharray="1.5 2" />
  </Rahmen>
);

/** Wetterschutzhaube: die Haube mit Wetterlamellen, sitzt außen. */
export const IconWetterhaube = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Wetterschutzhaube'}>
    <path d="M4 12l12-6 12 6" />
    <path d="M6 12v14h20V12" opacity={0.45} />
    <path d="M9 16h14M9 19.5h14M9 23h14" />
  </Rahmen>
);

/** Universalkomponente: leeres Gehäuse mit Zubehörpunkt. */
export const IconUniversal = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Universalkomponente'}>
    {Gehaeuse}
    <circle cx="16" cy="16" r="3.5" />
    <path d="M16 12.5v-2M16 21.5v2M12.5 16h-2M21.5 16h2" opacity={0.6} />
  </Rahmen>
);

/** Abstand: nur eine Leerkammer, deshalb ein Maßpfeil statt eines Bauteils. */
export const IconAbstand = (p: Eigenschaften) => (
  <Rahmen {...p} title={p.title ?? 'Abstand'}>
    {Gehaeuse}
    <path d="M8 16h16" />
    <path d="M10.5 13.5L8 16l2.5 2.5M21.5 13.5L24 16l-2.5 2.5" />
  </Rahmen>
);

/**
 * Das Sinnbild zu einer Einheitenart.
 *
 * Eine unbekannte Art bekommt das Universalzeichen und nicht nichts: Ein leerer
 * Platz sähe aus wie ein Fehler in der Darstellung, obwohl nur die Zuordnung
 * fehlt.
 */
export function IconEinheit({
  art, size = G, title,
}: { art: string } & Eigenschaften) {
  const p = { size, title };

  switch (art) {
    case 'RAHMEN': return <IconRahmen {...p} />;
    case 'KLAPPE': return <IconKlappe {...p} />;
    case 'FILTER': return <IconFilter {...p} />;
    case 'SCHALL': return <IconSchall {...p} />;
    case 'ERH': return <IconErhitzer {...p} />;
    case 'KUEHL': return <IconKuehler {...p} />;
    case 'CHANGE': return <IconChangeOver {...p} />;
    case 'TROPF': return <IconTropfen {...p} />;
    case 'VENT': return <IconVentilator {...p} />;
    case 'WRG': return <IconWrg {...p} />;
    case 'BEF': return <IconBefeuchter {...p} />;
    case 'WETTER': return <IconWetterhaube {...p} />;
    case 'ABSTAND': return <IconAbstand {...p} />;
    default: return <IconUniversal {...p} />;
  }
}

/** Die deutschen Bezeichnungen, an einer Stelle. */
export const EinheitText: Record<string, string> = {
  RAHMEN: 'Anschlussrahmen',
  KLAPPE: 'Klappe',
  FILTER: 'Filter',
  SCHALL: 'Schalldämpfer',
  ERH: 'Erhitzer',
  KUEHL: 'Kühler',
  CHANGE: 'Change-Over',
  TROPF: 'Tropfenabscheider',
  VENT: 'Ventilator',
  WRG: 'Wärmerückgewinnung',
  BEF: 'Befeuchter',
  UNIV: 'Universalkomponente',
  ABSTAND: 'Abstand',
  WETTER: 'Wetterschutzhaube',
};
